import React from "react";
import { SearchIcon } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Field, FieldGroup, FieldLabel } from "../../components/ui/field";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";

type AppCaseFiltersIslandProps = {
  q?: unknown;
  appType?: unknown;
  deviceType?: unknown;
  platformCapability?: unknown;
};

const appTypeOptions = [
  { value: "all", label: "全部" },
  { value: "frontend", label: "Frontend" },
  { value: "backend", label: "Backend" }
];

const deviceOptions = [
  { value: "all", label: "全部" },
  { value: "desktop", label: "Desktop" },
  { value: "mobile", label: "Mobile" }
];

const capabilityOptions = [
  { value: "all", label: "全部" },
  { value: "db", label: "DB" },
  { value: "data-form", label: "Data Form" },
  { value: "cms", label: "CMS" },
  { value: "auth", label: "Auth" },
  { value: "workflow", label: "Workflow" },
  { value: "asset-library", label: "Asset Library" },
  { value: "object-storage", label: "Object Storage" }
];

export function AppCaseFiltersIsland(props: AppCaseFiltersIslandProps) {
  const [q, setQ] = React.useState(readString(props.q));
  const [appType, setAppType] = React.useState(readSelectValue(props.appType));
  const [deviceType, setDeviceType] = React.useState(readSelectValue(props.deviceType));
  const [platformCapability, setPlatformCapability] = React.useState(
    readSelectValue(props.platformCapability)
  );

  return (
    <form
      className="mb-8 rounded-xl border border-border bg-background p-4 shadow-sm"
      onSubmit={(event) => {
        event.preventDefault();
        window.location.assign(buildAppCaseFilterHref({ q, appType, deviceType, platformCapability }));
      }}
    >
      <FieldGroup className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_0.8fr_0.8fr_1fr_auto]">
        <Field className="gap-1.5">
          <FieldLabel htmlFor="app-case-search">搜索</FieldLabel>
          <Input
            id="app-case-search"
            type="search"
            maxLength={100}
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="搜索标题、描述、标签"
            className="h-10"
          />
        </Field>
        <FilterSelect label="应用类型" value={appType} options={appTypeOptions} onValueChange={setAppType} />
        <FilterSelect label="设备" value={deviceType} options={deviceOptions} onValueChange={setDeviceType} />
        <FilterSelect label="平台能力" value={platformCapability} options={capabilityOptions} onValueChange={setPlatformCapability} />
        <Button type="submit" size="lg" className="mt-auto">
          <SearchIcon data-icon="inline-start" />
          搜索
        </Button>
      </FieldGroup>
    </form>
  );
}

function FilterSelect(props: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onValueChange: (value: string) => void;
}) {
  const triggerId = React.useId();
  return (
    <Field className="gap-1.5">
      <FieldLabel htmlFor={triggerId}>{props.label}</FieldLabel>
      <Select value={props.value} onValueChange={props.onValueChange}>
        <SelectTrigger id={triggerId} size="lg" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectGroup>
            {props.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  );
}

export function buildAppCaseFilterHref(args: {
  q: string;
  appType: string;
  deviceType: string;
  platformCapability: string;
}): string {
  const params = new URLSearchParams();
  if (args.q.trim()) params.set("q", args.q.trim());
  if (args.appType !== "all") params.set("app_type", args.appType);
  if (args.deviceType !== "all") params.set("device_type", args.deviceType);
  if (args.platformCapability !== "all") {
    params.set("platform_capability", args.platformCapability);
  }
  const search = params.toString();
  return search ? `/cases/?${search}` : "/cases/";
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readSelectValue(value: unknown): string {
  return typeof value === "string" && value ? value : "all";
}
